import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { produce } from 'immer';
import {
  SButton,
  SIcon,
  SInput,
  SNotification,
  SSelect,
  STable,
  STooltip,
} from '@chargebee/sting-react';
import { ConditionsGroup } from '@bbkAdminComponents/experiences/audiences/edit-rule/conditions-group';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import {
  integrationSlice,
  useSelectBillingIntegration,
} from '@bbkAdminRedux/rtkq/integrations.slice';
import {
  OptimizationActionType,
  OptimizationPriceAdjustmentType,
  OptimizationSliceHelper,
  type OptimizationAction,
} from '@bbkAdminRedux/rtkq/optimizations.slice';
import { cs } from '@bbkAdminUtils/css-utils';

type OptimizationActionFormProps = {
  value: OptimizationAction;
  onChange: (action: OptimizationAction) => void;
  actionsOnly?: boolean;
};

export const OptimizationActionForm: React.FC<OptimizationActionFormProps> = ({
  value: action,
  onChange,
  actionsOnly,
}) => {
  const appKey = useSelector(selectAppId);
  const billingIntegration = useSelectBillingIntegration({ appKey });

  const { data: planValues = [] } =
    integrationSlice.endpoints.loadFieldValues.useQuery(
      billingIntegration?.id && appKey
        ? {
            appKey,
            fieldName: 'subscription.plan_id',
            integrationId: billingIntegration.id,
            text: '',
          }
        : skipToken
    );

  const plansOptions = useMemo(
    () =>
      planValues.map((p) => ({
        value: p.key,
        label: p.display_name,
      })),
    [planValues]
  );

  const isPercentageAdj =
    action.type === OptimizationActionType.PLAN_PRICE_ADJUSTMENT &&
    (action.adjustment_type ===
      OptimizationPriceAdjustmentType.DECREASE_BY_PERCENTAGE ||
      action.adjustment_type ===
        OptimizationPriceAdjustmentType.INCREASE_BY_PERCENTAGE);

  const renderMenu = (idx: number) => {
    const hasOnlyOnePlan =
      action.type === OptimizationActionType.PLAN_CHANGE &&
      (action.plans?.length ?? 0) <= 1;

    const handleRemove = () => {
      if (action.type !== OptimizationActionType.PLAN_CHANGE) return;
      onChange(
        produce(action, (draft) => {
          draft.plans?.splice(idx, 1);
        })
      );
    };

    const trashButton = (
      <SButton
        variant="danger-ghost"
        icon={<SIcon name="trash-2" />}
        onClick={handleRemove}
        disabled={hasOnlyOnePlan}
        className="!tw-border-transparent"
      />
    );

    return hasOnlyOnePlan ? (
      <STooltip
        label="At least one plan must remain"
        delayDuration={400}
        placement="left"
        color="light"
      >
        <div>{trashButton}</div>
      </STooltip>
    ) : (
      trashButton
    );
  };

  const renderedActions =
    action.type === OptimizationActionType.PLAN_PRICE_ADJUSTMENT ? (
      <>
        <div>
          <SSelect
            className="tw-w-full"
            value={action.adjustment_type}
            onValueChange={(val) => {
              onChange(
                produce(action, (draft) => {
                  draft.adjustment_type =
                    val as unknown as OptimizationPriceAdjustmentType;
                })
              );
            }}
            clearable={false}
            searchable={false}
            options={[
              {
                label: 'Increase by percentage',
                value: OptimizationPriceAdjustmentType.INCREASE_BY_PERCENTAGE,
              },
              {
                label: 'Increase by fixed amount',
                value: OptimizationPriceAdjustmentType.INCREASE_BY_FIXED_AMOUNT,
              },
              {
                label: 'Decrease by percentage',
                value: OptimizationPriceAdjustmentType.DECREASE_BY_PERCENTAGE,
              },
              {
                label: 'Decrease by fixed amount',
                value: OptimizationPriceAdjustmentType.DECREASE_BY_FIXED_AMOUNT,
              },
            ]}
            label="Plan Adjustment Type"
          />
        </div>
        <div>
          <SInput
            className="tw-w-full"
            onChange={(e) => {
              const val = parseFloat(e.currentTarget.value);

              onChange(
                produce(action, (draft) => {
                  draft.adjustment_value =
                    Number.isNaN(val) || val < 0 ? 0 : val;
                })
              );
            }}
            value={`${action.adjustment_value}`}
            label={isPercentageAdj ? 'Percentage' : 'Adjustment amount'}
            type="number"
            trailingIcon={
              isPercentageAdj ? (
                <SIcon name="percent" />
              ) : (
                <span className="tw-text-sm tw-text-neutral-400">
                  per subscription currency
                </span>
              )
            }
          />
        </div>
        {!isPercentageAdj && (
          <div className="tw-col-span-2">
            <SNotification
              icon={null}
              title="Amount is applied in each subscription’s billing currency. For example, 10 becomes $10 for USD plans, €10 for EUR plans, etc."
              variant="warning"
            />
          </div>
        )}
      </>
    ) : action.type === OptimizationActionType.PLAN_CHANGE ? (
      <div className="tw-col-span-2">
        <STable layout="fixed">
          <STable.Head>
            <STable.Row>
              <STable.HeaderCell className="tw-w-5/12">
                Current Plan
              </STable.HeaderCell>
              <STable.HeaderCell className="tw-w-5/12">
                Target Plan
              </STable.HeaderCell>
              <STable.HeaderCell />
            </STable.Row>
          </STable.Head>
          <STable.Body>
            {action.plans?.map((pl, idx) => (
              <STable.Row key={idx}>
                <STable.Cell>
                  <SSelect
                    options={plansOptions}
                    value={pl.current_plan}
                    onValueChange={(e) => {
                      onChange(
                        produce(action, (draft) => {
                          const val = e as string;
                          draft.plans ??= [];
                          draft.plans[idx] ??=
                            OptimizationSliceHelper.createNewPlanChangePlan();
                          draft.plans[idx].current_plan = val;
                        })
                      );
                    }}
                  />
                </STable.Cell>
                <STable.Cell>
                  <SSelect
                    options={plansOptions}
                    value={pl.target_plan}
                    onValueChange={(e) => {
                      onChange(
                        produce(action, (draft) => {
                          const val = e as string;
                          draft.plans ??= [];
                          draft.plans[idx] ??=
                            OptimizationSliceHelper.createNewPlanChangePlan();
                          draft.plans[idx].target_plan = val;
                        })
                      );
                    }}
                  />
                </STable.Cell>
                <STable.Cell>{renderMenu(idx)}</STable.Cell>
              </STable.Row>
            ))}
          </STable.Body>
          <STable.Footer>
            <STable.Row>
              <STable.Cell colSpan={2}>
                <SButton
                  variant="primary-ghost"
                  icon={<SIcon name="plus" />}
                  onClick={() => {
                    onChange(
                      produce(action, (draft) => {
                        draft.plans ??= [];
                        draft.plans[draft.plans.length] =
                          OptimizationSliceHelper.createNewPlanChangePlan();
                      })
                    );
                  }}
                >
                  Add Another Plan
                </SButton>
              </STable.Cell>
            </STable.Row>
          </STable.Footer>
        </STable>
      </div>
    ) : null;

  return (
    <>
      {!actionsOnly && (
        <>
          <div className="tw-p-6">
            <SInput
              value={action.display_name}
              onChange={(e) => {
                const val = e.currentTarget.value;
                onChange(
                  produce(action, (draft) => {
                    draft.display_name = val;
                  })
                );
              }}
              label="Rule Name"
              placeholder="Enter rule name..."
            />
          </div>
          <div className="tw-p-6">
            <ConditionsGroup path={['audience']} maxLevels={1} />
          </div>
        </>
      )}
      <div className={cs({ 'tw-p-6': !actionsOnly })}>
        {!actionsOnly && <div className="tw-mb-sm tw-text-xs">Actions</div>}
        <div className="tw-grid tw-grid-cols-2 tw-gap-sm">
          <div>
            <SSelect
              className="tw-w-full"
              value={action.type}
              onValueChange={(val) => {
                onChange(
                  produce(action, (draft) => {
                    draft.type = val as unknown as OptimizationActionType;
                    OptimizationSliceHelper.cleanupActionFields(draft);
                    if (
                      draft.type ===
                      OptimizationActionType.PLAN_PRICE_ADJUSTMENT
                    ) {
                      draft.adjustment_type ??=
                        OptimizationPriceAdjustmentType.INCREASE_BY_PERCENTAGE;
                      draft.adjustment_value ??= 10;
                    } else {
                      draft.plans ??= [
                        OptimizationSliceHelper.createNewPlanChangePlan(),
                      ];
                    }
                  })
                );
              }}
              clearable={false}
              searchable={false}
              options={[
                {
                  label: 'Plan Price Adjustment',
                  value: OptimizationActionType.PLAN_PRICE_ADJUSTMENT,
                },
                {
                  label: 'Plan Change',
                  value: OptimizationActionType.PLAN_CHANGE,
                },
              ]}
              label="Type"
            />
          </div>
          {renderedActions}
        </div>
      </div>
    </>
  );
};
